import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../context/AuthContext';
import PaymentSuccessModal from './PaymentSuccessModal';
import ContactFormModal from './ContactFormModal';
import RichTextEditor from './RichTextEditor';
import colors from '../theme/colors';
import { IconInfo, IconBook, IconCheck, IconCross, IconLock, IconBulb, IconMail, IconHeart, replaceEmojisWithIcons } from './icons';

const ButtonContainer = styled.div`
  position: fixed;
  top: 70px;
  left: 20px;
  z-index: 1000;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  /* Mobile: relative positioning when inside ButtonsContainer */
  @media (max-width: 768px) {
    position: static;
    display: contents;
  }
`;

const AboutButton = styled(motion.button)`
  background: ${colors.button.aboutBg};
  border: 1px solid ${colors.button.aboutBorder};
  border-radius: 20px;
  color: ${colors.button.aboutText};
  padding: 8px 12px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  box-shadow: ${colors.effects.shadow};

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-1px);
    box-shadow: ${colors.effects.shadowHover};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.background.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 12000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  /* Same treatment as the main header: translucent theme blue + white streaks */
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  border-radius: 16px;
  padding: 10px;
  /* Clear the absolute close button (top 20px + 36px tall) so page text never runs under it */
  padding-top: 60px;
  max-width: 860px;
  width: 100%;
  max-height: 88vh;
  ${props => props.$scrollInner && `height: 88vh;`}

  @media (max-width: 768px) {
    max-height: 92vh;
    ${props => props.$scrollInner && `height: 92vh;`}
  }
  overflow-y: ${props => (props.$paginated || props.$scrollInner) ? 'hidden' : 'auto'};
  ${props => props.$scrollInner && `
    display: flex;
    flex-direction: column;
  `}
  position: relative;
  box-shadow: ${colors.effects.shadowLarge};
  border: 1px solid ${colors.border.light};

  /* Optimize scrolling performance */
  will-change: transform;
  transform: translateZ(0);
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.ui.scrollbarTrackDark};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.ui.scrollbarThumb};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.ui.scrollbarThumbHover};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: ${colors.background.white10};
  border: 1px solid ${colors.border.light};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.white20};
    transform: rotate(90deg);
  }
`;

const ModalTitle = styled.h2`
  color: ${colors.accent.primary};
  margin: 0 0 20px 0;
  font-size: 1.6rem;
  font-weight: 700;
  text-shadow: ${colors.effects.textShadowGlow};
  text-align: left;
`;

const ModalText = styled.div`
  color: ${colors.text.light};
  line-height: 1.8;
  font-size: ${props => props.$large ? '1rem' : '0.7rem'};
  margin-bottom: 0px;
  text-align: ${props => props.$center ? 'center' : 'left'};

  p {
    margin: 0 0 16px 0;
    text-align: ${props => props.$center ? 'center' : 'left'};
  }

  strong {
    color: ${colors.accent.primary};
    font-weight: 600;
  }

  ul {
    text-align: left;
    padding-left: 30px;
    font-size: ${props => props.$large ? '0.85rem' : '0.7rem'};
    margin-top: 12px;
    margin-bottom: 16px;
    line-height: 1.8;
  }

  li {
    margin-bottom: 8px;
  }

  h3 {
    text-align: center;
    font-size: ${props => props.$large ? '1.3rem' : '1.1rem'};
    color: ${colors.accent.secondary};
    margin-bottom: 16px;
  }
`;

const WhiteContentBox = styled.div`
  padding: 10px;
  background: ${colors.base.themeTertiary};
  border-radius: 12px;
  box-shadow: ${colors.effects.shadow};
  margin: 5px 0;
  font-size: 0.9rem;
  position: relative;

  ${props => props.$scrollInner && `
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `}

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.08);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.base.themePrimary};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.base.themePrimaryVariant};
  }

  h3 {
    color: ${colors.base.themePrimary};
    margin: 0 0 16px 0;
    font-size: 1.2rem;
    font-weight: 700;
    text-align: center;
  }

  p {
    color: ${colors.base.themePrimary};
    margin: 0 0 4px 0;
    font-size: 0.85rem;
    line-height: 1.3;
    text-align: left;
  }

  ul, ol {
    text-align: left;
    padding-left: 20px;
    color: ${colors.base.themePrimary};
    margin: 12px 0;
    column-count: ${props => props.$twoColumns ? '2' : '1'};
    column-gap: 20px;

    li {
      margin-bottom: 3px;
      color: ${colors.base.themePrimary};
      break-inside: avoid;

      &::marker {
        color: ${colors.base.themePrimary};
        font-size: 1.2em;
      }
    }
  }

  strong {
    color: ${colors.base.themePrimary};
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  table {
    border-collapse: collapse;
    margin: 12px 0;
    width: 100%;
    font-size: 0.85rem;
  }

  table.bordered th,
  table.bordered td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }

  table.borderless th,
  table.borderless td {
    border: none;
    padding: 8px 12px 8px 0;
    text-align: left;
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
    color: ${colors.base.themePrimary};
  }

  td {
    color: ${colors.base.themePrimary};
  }
`;

const DonateSection = styled.div`
  padding: 25px;
  background: ${colors.base.themeTertiary};
  border-radius: 12px;
  text-align: center;
  box-shadow: ${colors.effects.shadow};
  margin: 0px 30px 0 30px;

  h3 {
    color: ${colors.base.themePrimary};
    margin: 0 0 15px 0;
    font-size: 1.1rem;
    font-weight: 600;
    text-align: center;
  }

  p {
    color: ${colors.base.themePrimary};
    margin: 0 0 12px 0;
    font-size: 0.7rem;
    text-align: justify;
  }

  ul {
    text-align: left;
    margin: 0 auto;
    display: inline-block;
    color: ${colors.base.themePrimary};
  }

  /* Force PayPal buttons to full width */
  .paypal-button {
    width: 100% !important;
    max-width: none !important;
  }

  [id*="paypal-container"] {
    width: 100% !important;
  }

  [id*="paypal-container"] iframe {
    width: 100% !important;
    max-width: none !important;
  }

  .paypal-container {
    width: 100% !important;
  }

  .paypal-container iframe {
    width: 100% !important;
    max-width: none !important;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ContactButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${colors.button.primaryBg};
  color: ${colors.button.primaryText};
  border-radius: 6px;
  border: 1px solid ${colors.button.primaryBorder};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.button.primaryBgHover};
    color: ${colors.button.primaryText};
    border-color: ${colors.base.accentPrimary};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EditStoryButton = styled.button`
  position: absolute;
  top: 20px;
  right: 70px;
  background: ${colors.button.primaryBg};
  border: 1px solid ${colors.button.primaryBorder};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  color: ${colors.button.primaryText};
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.button.primaryBgHover};
    transform: translateY(-1px);
  }
`;

const StoryEditModal = styled(motion.div)`
  position: fixed;
  top: 50%;
  left: 50%;
  background: ${colors.base.themePrimaryTransparent};
  border-radius: 16px;
  padding: 30px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 13000;
  box-shadow: ${colors.effects.shadowLarge};
  border: 1px solid ${colors.border.light};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.ui.scrollbarTrackDark};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.ui.scrollbarThumb};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.ui.scrollbarThumbHover};
  }

  h3 {
    color: ${colors.accent.primary};
    margin: 0 0 20px 0;
    font-size: 1.5rem;
  }

  label {
    display: block;
    color: ${colors.text.light};
    margin: 15px 0 5px 0;
    font-weight: 600;
  }

  input, textarea {
    width: 100%;
    padding: 10px;
    background: ${colors.background.white10};
    border: 1px solid ${colors.border.light};
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    background: ${colors.background.white};
    
    &:focus {
      outline: none;
      border-color: ${colors.accent.primary};
    }
  }

  textarea {
    min-height: 300px;
    resize: vertical;
  }
`;

const EditModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const EditActionButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  border: 1px solid;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary ? `
    background: ${colors.button.primaryBg};
    border-color: ${colors.button.primaryBorder};
    color: ${colors.button.primaryText};
    
    &:hover {
      background: ${colors.button.primaryBgHover};
      transform: translateY(-1px);
    }
  ` : `
    background: transparent;
    border-color: ${colors.border.light};
    color: ${colors.text.light};
    
    &:hover {
      background: ${colors.background.white10};
    }
  `}
`;


const ArrowButton = styled.button`
  position: absolute;
  Bottom: 0px;
  transform: translateY(-50%);
  background: ${colors.background.white10};
  border: 1px solid ${colors.border.lightMedium};
  border-radius: 50%;
  width: 50px;
  height: 50px;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}

  &:hover {
    background: ${colors.button.aboutBgHover};
    transform: translateY(-50%) scale(1.15);
    box-shadow: ${colors.effects.shadowLarge};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

const PageContent = styled(motion.div)`
  max-height: 60vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.ui.scrollbarTrackDark};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.ui.scrollbarThumb};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.ui.scrollbarThumbHover};
  }
`;

const ContentWrapper = styled.div`
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0 5px;
  }
`;

/* Fills remaining height inside a $scrollInner ModalContent; lets its flex child (the white box) stretch to full height */
const ScrollableBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TwoColumnList = styled.ul`
  column-count: 2;
  column-gap: 30px;
  padding-left: 20px;
  margin: 16px 0;
  font-size: 0.85rem;
  li {
    margin-bottom: 12px;
    break-inside: avoid;
    line-height: 1.3;
  }

  @media (max-width: 768px) {
    column-count: 1;
  }
`;

const PageIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  padding-top: 40px;
`;

const PageHint = styled.div`
  text-align: center;
  color: ${colors.text.light};
  font-size: 0.75rem;
  margin-top: 8px;
  opacity: 0.7;
`;

const Ripple = styled.div`
  position: fixed;
  width: 50px;
  height: 50px;
  border: 2px solid ${colors.accent.primary};
  border-radius: 50%;
  pointer-events: none;
  z-index: 12001;
  animation: ripple-expand 2s ease-out infinite;
  
  @keyframes ripple-expand {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

const CloseButtonRipple = styled(Ripple)`
  width: 36px;
  height: 36px;
  
  @keyframes ripple-expand {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    100% {
      transform: scale(2.2);
      opacity: 0;
    }
  }
`;

const PageDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$active ? colors.ui.dotActive : colors.ui.dotInactive};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.accent.secondary};
  }
`;

const AdminToggleButton = styled(motion.button)`
  background: ${props => props.$active ? colors.button.adminActiveBg : colors.button.adminInactiveBg};
  border: 1px solid ${props => props.$active ? colors.button.adminActiveBorder : colors.button.adminInactiveBorder};
  border-radius: 20px;
  color: ${colors.button.adminText};
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  box-shadow: ${colors.effects.shadow};

  &:hover {
    background: ${props => props.active ? colors.button.adminActiveBgHover : colors.button.adminInactiveBgHover};
    border-color: ${props => props.active ? colors.button.adminActiveBorder : colors.border.lightHover};
    transform: translateY(-1px);
    box-shadow: ${colors.effects.shadowHover};
  }

  &:active {
    transform: translateY(0);
  }
`;

// ---- Disclaimer column icons (line-art, inherit color via currentColor) ----
const IconTree = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M13 4v15" /><path d="M13 6c-3 0-5 2-5 4.5S10 14 13 14" /><path d="M13 9c3 0 5 1.8 5 4S16 16.5 13 16.5" /><path d="M9 22h8" />
  </svg>
);
const IconRobot = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="6" y="8" width="14" height="10" rx="2.5" /><circle cx="10.5" cy="13" r="1.1" fill="currentColor" stroke="none" /><circle cx="15.5" cy="13" r="1.1" fill="currentColor" stroke="none" />
    <path d="M13 8V5.5" strokeLinecap="round" /><circle cx="13" cy="4.5" r="1" fill="currentColor" stroke="none" /><path d="M9 18v1.5M17 18v1.5" strokeLinecap="round" />
  </svg>
);
const IconStopwatch = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="13" cy="14" r="7.5" /><path d="M13 14V9.5M13 14l3.5 2" strokeLinecap="round" /><path d="M10.5 3.5h5M13 3.5v2.2" strokeLinecap="round" />
  </svg>
);
const IconAvatar = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="13" cy="9.5" r="3.5" /><path d="M6 21c0-4 3.1-6.5 7-6.5s7 2.5 7 6.5" strokeLinecap="round" />
  </svg>
);
const IconNoDiagnosis = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M13 6v9M9.5 9.5h7" strokeLinecap="round" /><path d="M13 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
    <path d="M9 15c0 2.5 1.8 4.5 4 4.5s4-2 4-4.5" strokeLinecap="round" /><line x1="5" y1="21" x2="21" y2="5" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconNoCertified = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="13" cy="10" r="5" /><path d="M10 14.5 8.5 22l4.5-2.5 4.5 2.5-1.5-7.5" strokeLinejoin="round" /><line x1="5" y1="21" x2="21" y2="5" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconNoCoaching = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M13.5 5h7a1.8 1.8 0 0 1 1.8 1.8v4.4A1.8 1.8 0 0 1 20.5 13h-3.2l-2.6 2.2V13h-1.2a1.8 1.8 0 0 1-1.8-1.8V6.8A1.8 1.8 0 0 1 13.5 5Z" strokeLinejoin="round" />
    <circle cx="7.5" cy="12.5" r="2.8" /><path d="M2.5 21c0-2.9 2.2-4.7 5-4.7s5 1.8 5 4.7" strokeLinecap="round" />
    <line x1="5" y1="21" x2="21" y2="5" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconDocument = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M8 4h8l3 3v15H8V4Z" strokeLinejoin="round" /><path d="M16 4v3h3" strokeLinejoin="round" /><path d="M10.5 13h6M10.5 16h6M10.5 19h3.5" strokeLinecap="round" />
  </svg>
);
const IconIncludeHeader = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M6 15c0-4.4 3.6-8 8-8h1a4 4 0 0 1 0 8h-.5" strokeLinecap="round" /><path d="M7 15v5.5M7 15h9" strokeLinecap="round" /><circle cx="16.5" cy="9" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);
const IconExcludeHeader = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M13 4c-3 3-3.5 5.5-3.5 8.5V16l-2 2.5h11L16.5 16v-3.5C16.5 9.5 16 7 13 4Z" strokeLinejoin="round" /><path d="M10.5 19a2.5 2.5 0 0 0 5 0" strokeLinecap="round" />
  </svg>
);

const DisclaimerColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 10px 0 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DisclaimerColumn = styled.div`
  border-radius: 14px;
  padding: 18px 16px;
  background: ${props => props.$variant === 'exclude' ? '#fbeaec' : '#e7f3ea'};
  border: 1px solid ${props => props.$variant === 'exclude' ? '#f0c7cc' : '#bcdec4'};

  /* Mobile: "You will not find" reads first since not everything fits on screen */
  @media (max-width: 768px) {
    order: ${props => props.$variant === 'exclude' ? 1 : 2};
  }

  display: flex;
  flex-direction: column;
`;

const DisclaimerColumnTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${props => props.$variant === 'exclude' ? '#7a2530' : '#1f4d34'};

  svg { flex: none; width: 22px; height: 22px; }
`;

const DisclaimerTileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DisclaimerTile = styled.div`
  border: 1px solid ${props => props.$variant === 'exclude' ? '#f0c7cc' : '#bcdec4'};
  border-radius: 12px;
  padding: 14px 10px;
  text-align: center;
  background: #fffdf9;

  /* Odd tile out: spans the row rather than leaving a hole beside it */
  ${props => props.$wide && 'grid-column: 1 / -1;'}
`;

const DisclaimerTileIcon = styled.div`
  width: 28px;
  height: 28px;
  margin: 0 auto 8px;
  color: ${props => props.$variant === 'exclude' ? '#7a2530' : '#1f4d34'};

  svg { width: 100%; height: 100%; }
`;

const DisclaimerTileTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${props => props.$variant === 'exclude' ? '#7a2530' : '#1f4d34'};
  margin-bottom: 5px;
`;

const DisclaimerTileBody = styled.div`
  font-size: 0.7rem;
  line-height: 1.4;
  color: ${colors.text.dark};
`;

const LoggedInBox = styled.div`
  margin-top: auto;
  border: 1.5px solid ${colors.accent.secondary};
  background: linear-gradient(180deg, #fffaf0 0%, #fdf5e2 100%);
  border-radius: 12px;
  padding: 12px 14px 14px;
`;

const LoggedInTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #8a6a20;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 5px;

  svg { flex: none; }
`;

const PillRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
`;

const Pill = styled.div`
  flex: 1 1 0;
  min-width: 0;
  border: 1px solid #e3cf9a;
  background: #fffdf9;
  color: #7a5c1c;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 6px 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  svg { flex: none; }
`;

const DocLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.7rem;
  color: #6b5218;
  padding-top: 10px;
  border-top: 1px dashed rgba(138, 106, 32, 0.35);

  svg { flex: none; width: 16px; height: 16px; }
`;

// ---- Contribution tile icons ----
const IconFeedback = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 7.5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H10l-3.5 3v-3H6a2 2 0 0 1-2-2v-5Z" strokeLinejoin="round" />
    <circle cx="19.5" cy="17" r="2.6" />
    <path d="M19.5 13.9v-1M19.5 20.1v-1M22.6 17h1M15.4 17h-1M21.5 15l.7-.7M17.3 19.7l.7-.7M21.5 19l.7.7M17.3 14.3l.7.7" strokeLinecap="round" />
  </svg>
);
const IconIdeas = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M11.5 3a5.5 5.5 0 0 0-3.2 10c.6.4.9 1 .9 1.7v1h5.6v-1c0-.7.3-1.3.9-1.7a5.5 5.5 0 0 0-3.2-10Z" strokeLinejoin="round" />
    <path d="M9.3 17.7h4.4M9.7 20h3.6" strokeLinecap="round" />
    <circle cx="20" cy="8" r="2.2" />
    <path d="M20 4.8v-1M20 12.2v-1M23.2 8h1M15.8 8h-1M22.3 5.7l.7-.7M16.9 10.3l.7-.7M22.3 10.3l.7.7M16.9 5.7l.7.7" strokeLinecap="round" />
  </svg>
);
const IconMegaphone = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 10.5v3a1.5 1.5 0 0 0 1.5 1.5H7l8.5 4.5V6L7 10.5H5.5A1.5 1.5 0 0 0 4 12Z" strokeLinejoin="round" />
    <path d="M18.5 8.5c1.3 1 1.3 4 0 5" strokeLinecap="round" />
    <path d="M7.5 15.5 8.5 20" strokeLinecap="round" />
  </svg>
);
const IconServer = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="5" width="18" height="6.5" rx="1.5" />
    <rect x="4" y="14.5" width="18" height="6.5" rx="1.5" />
    <path d="M7.5 8.25h.01M7.5 17.75h.01" strokeLinecap="round" strokeWidth="2" />
    <path d="M12 8.25h6M12 17.75h6" strokeLinecap="round" />
  </svg>
);
const IconSparkles = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M11.5 4.5 13.1 9l4.5 1.6-4.5 1.6-1.6 4.5-1.6-4.5-4.5-1.6L9.9 9l1.6-4.5Z" strokeLinejoin="round" />
    <path d="m19.5 15.5.9 2.4 2.4.9-2.4.9-.9 2.4-.9-2.4-2.4-.9 2.4-.9.9-2.4Z" strokeLinejoin="round" />
  </svg>
);

const ProblemPanel = styled.div`
  background: #faf7ef;
  border-radius: 16px;
  padding: 5px 20px;
  margin: 4px 0 14px;
`;

const ProblemHeader = styled.h3`
  margin: 14px 0;
  font-size: 1.1rem;
  font-weight: 700;
  text-align: center;
  color: ${colors.base.themePrimary};
  text-wrap: balance;
`;

const ProblemParagraph = styled.p`
  margin: 0 0 12px;
  font-size: 0.82rem;
  line-height: 1.55;
  text-align: left;
  color: ${colors.base.themePrimary};
`;

const ContributePanel = styled.div`
  background: #faf7ef;
  border-radius: 16px;
  padding: 22px 20px;
`;

const ContributeHeader = styled.h3`
  margin: 0 0 12px;
  font-size: 1.1rem;
  font-weight: 700;
  text-align: center;
  color: ${colors.base.themePrimary};
`;

const ContributeIntro = styled.p`
  margin: 0 0 16px;
  font-size: 0.8rem;
  line-height: 1.5;
  text-align: left;
  color: ${colors.base.themePrimary};
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.div`
  border: 1px solid rgba(30, 27, 61, 0.15);
  border-radius: 12px;
  padding: 14px 10px;
  text-align: center;
  background: ${colors.base.themeTertiary};
`;

const TileIcon = styled.div`
  width: 28px;
  height: 28px;
  margin: 0 auto 8px;
  color: ${colors.base.themePrimary};

  svg { width: 100%; height: 100%; }
`;

const TileTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${colors.base.themePrimary};
  margin-bottom: 5px;
`;

const TileBody = styled.div`
  font-size: 0.7rem;
  line-height: 1.4;
  color: ${colors.text.dark};
`;

const AccentContactButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;
  background: ${colors.base.themePrimary};
  color: ${colors.accent.primary};
  border: 1px solid ${colors.accent.secondary};
  border-radius: 24px;
  padding: 10px 22px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 0 14px rgba(255, 255, 255, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  }
`;

const AboutMeButton = ({ autoOpen = false, onAutoOpenHandled, isDevelopment = false, isAdmin = false, toggleAdminMode = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isStoryOpen, setIsStoryOpen] = useState(false);
  const [isStoryEditOpen, setIsStoryEditOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [aboutContent, setAboutContent] = useState([]);
  const [storyData, setStoryData] = useState(null);
  const [storyEditData, setStoryEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storyLoading, setStoryLoading] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [wasAutoOpened, setWasAutoOpened] = useState(false);
  const [rightArrowPos, setRightArrowPos] = useState(null);
  const [closeButtonPos, setCloseButtonPos] = useState(null);
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { user } = useAuth();

  // Swipe gesture handling
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const rightArrowRef = useRef(null);
  const closeButtonRef = useRef(null);
  const minSwipeDistance = 50; // Minimum distance for a swipe to register
  const totalPages = 3;

  // Auto-open on first visit (either first-time anonymous user or first login)
  useEffect(() => {
    if (autoOpen && !isOpen) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        setIsOpen(true);
        setWasAutoOpened(true);
        // Inform parent that auto open has been handled so it doesn't re-trigger
        if (typeof onAutoOpenHandled === 'function') {
          onAutoOpenHandled();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, isOpen, onAutoOpenHandled]);

  // Track button positions for ripple effects
  useEffect(() => {
    if (isOpen && wasAutoOpened) {
      const updatePositions = () => {
        if (rightArrowRef.current) {
          const rect = rightArrowRef.current.getBoundingClientRect();
          setRightArrowPos({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        }
        if (closeButtonRef.current) {
          const rect = closeButtonRef.current.getBoundingClientRect();
          setCloseButtonPos({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        }
      };
      
      // Update positions after modal animation completes (wait for framer-motion animation)
      const timer1 = setTimeout(updatePositions, 100);
      const timer2 = setTimeout(updatePositions, 300);
      const timer3 = setTimeout(updatePositions, 500);
      
      // Update on resize and scroll
      window.addEventListener('resize', updatePositions);
      window.addEventListener('scroll', updatePositions);
      
      // Use requestAnimationFrame to continuously update during animations
      let animationFrameId;
      const animate = () => {
        updatePositions();
        animationFrameId = requestAnimationFrame(animate);
      };
      animationFrameId = requestAnimationFrame(animate);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        window.removeEventListener('resize', updatePositions);
        window.removeEventListener('scroll', updatePositions);
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }
  }, [isOpen, wasAutoOpened, currentPage]);

  // Fetch about content from database
  useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        const response = await fetch('/api/about/content');
        const data = await response.json();
        
        if (data.success && data.content) {
          setAboutContent(data.content);
        }
      } catch (error) {
        console.error('Error fetching about content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutContent();
  }, []);

  // Fetch My Story content from database
  useEffect(() => {
    const fetchStoryContent = async () => {
      try {
        const response = await fetch('/api/story/content');
        const data = await response.json();
        
        if (data.success && data.story) {
          setStoryData(data.story);
        }
      } catch (error) {
        console.error('Error fetching story content:', error);
      } finally {
        setStoryLoading(false);
      }
    };

    fetchStoryContent();
  }, []);

  // PayPal config is now handled directly in the useEffect below

  // Load PayPal SDK when on page 3 (donation page)
  useEffect(() => {
    const cleanup = () => {
      const container = document.getElementById('paypal-about-container');
      if (container) container.innerHTML = '';
    };

    // Only initialize PayPal when modal is open AND on page 3 (index 2)
    if (!isOpen || currentPage !== 2) {
      cleanup();
      return cleanup;
    }

    console.log('Loading PayPal SDK for About Me Button');

    // Add a small delay to ensure DOM is ready after page transition
    const timer = setTimeout(() => {
      (async () => {
        // Load SDK once globally
        const ensureSdk = async () => {
          if (window.paypal && window.paypal.Buttons) return;
          if (window.__paypalSdkPromise) { await window.__paypalSdkPromise; return; }
          window.__paypalSdkPromise = (async () => {
            let sdkUrl = 'https://www.paypal.com/sdk/js?client-id=test&components=buttons&currency=CAD';
            try {
              const res = await fetch('/api/config/paypal-url');
              const data = await res.json();
              if (data && data.sdkUrl) sdkUrl = data.sdkUrl;
              console.log('PayPal SDK URL resolved to:', sdkUrl);
            } catch (e) {
              console.warn('Falling back to PayPal demo client-id=test due to config fetch error:', e);
            }
            await new Promise((resolve, reject) => {
              let script = document.getElementById('paypal-sdk');
              if (!script) {
                script = document.createElement('script');
                script.id = 'paypal-sdk';
                script.src = sdkUrl;
                script.async = true;
                script.onload = resolve;
                script.onerror = reject;
                document.body.appendChild(script);
              } else {
                if (window.paypal && window.paypal.Buttons) resolve();
                else script.addEventListener('load', resolve, { once: true });
              }
            });
          })();
          await window.__paypalSdkPromise;
        };

        await ensureSdk();

        console.log('PayPal SDK loaded successfully');
        if (window.paypal && window.paypal.Buttons) {
            console.log('Rendering PayPal button for About Me');
            const container = document.getElementById('paypal-about-container');
            if (container) {
              container.innerHTML = '';
              window.paypal.Buttons({
                fundingSource: window.paypal.FUNDING.PAYPAL,
                createOrder: function(data, actions) {
                  const amountInput = document.getElementById('donation-amount');
                  const selectedAmount = amountInput && amountInput.value ? amountInput.value : '1.00';
                  const customData = user?.userUuid ? user.userUuid : null;
                  console.log('Creating PayPal order with custom_id (userUuid):', customData, 'amount:', selectedAmount);
                  return actions.order.create({
                    intent: 'CAPTURE',
                    purchase_units: [{
                      amount: { value: selectedAmount, currency_code: 'CAD' },
                      custom_id: customData
                    }]
                  });
                },
                onApprove: function(data, actions) {
                  return actions.order.capture().then(async function(details) {
                    console.log('Payment completed:', details);

                    // Extract payer info and send to backend
                    const payerEmail = details.payer?.email_address;
                    const payerName = details.payer?.name?.given_name
                      ? `${details.payer.name.given_name} ${details.payer.name.surname || ''}`.trim()
                      : null;
                    const transactionId = details.purchase_units?.[0]?.payments?.captures?.[0]?.id || details.id;

                    console.log('Payer info:', { payerEmail, payerName, transactionId });

                    // Update payment with payer info
                    if (transactionId && (payerEmail || payerName)) {
                      try {
                        await fetch('/api/payments/update-payer', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ transactionId, payerEmail, payerName })
                        });
                      } catch (err) {
                        console.warn('Could not update payer info:', err);
                      }
                    }

                    setIsOpen(false);
                    setShowThankYou(true);
                  });
                },
                onError: (err) => {
                  console.error('PayPal Buttons error:', err);
                  alert('PayPal failed to initialize. Please refresh and try again.');
                }
              }).render('#paypal-about-container');
            } else {
              console.warn('PayPal container not found');
            }
        } else {
          console.error('PayPal Buttons not available');
        }
      })();
    }, 400); // Wait for page transition animation to complete

    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [isOpen, currentPage, user?.googleId, user?.userUuid]);

  // Close About Me modal with Escape key (only on last page if auto-opened)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && (!wasAutoOpened || currentPage === 2)) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, currentPage, wasAutoOpened, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    // If modal was auto-opened and user is not authenticated, mark as seen in localStorage
    if (wasAutoOpened && !user) {
      localStorage.setItem('hasSeenWelcome', 'true');
      console.log('💾 Saved welcome modal as seen in localStorage (anonymous user)');
    }
    // For authenticated users, the API already handles setting welcome=0 in database
    
    setIsOpen(false);
    setCurrentPage(0); // Reset to first page when closing
    setWasAutoOpened(false); // Reset flag
  };

  const handleOpen = () => {
    setIsOpen(true);
    setCurrentPage(0); // Start at first page when opening
  };

  const handleTestFirstTime = () => {
    setWasAutoOpened(true);
    setIsOpen(true);
    setCurrentPage(0);
  };


  // Helper function to parse **bold** markdown syntax in text
  const makeBoldWords = (text) => {
    // Capitalize first letter
    const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1);

    // Replace **text** with bold styling
    const result = capitalizedText.replace(
      /\*\*([^*]+)\*\*/g,
      '<strong style="color: #030303; font-weight: 600;">$1</strong>'
    );

    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  // Render content based on type and language
  const renderContent = (section, useTwoColumns = false) => {
    const contentLang = currentLanguage?.startsWith('fr') ? 'fr' : 'en';
    const title = section[`title_${contentLang}`];
    const content = section[`content_${contentLang}`];

    switch (section.section_type) {
      case 'title':
        if (section.section_key === 'main_title') {
          return <ModalTitle key={section.id}>{title}</ModalTitle>;
        }
        if (section.section_key === 'help_title') {
          return (
            <h3 key={section.id}>
              <IconHeart size={16} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
              {title}
            </h3>
          );
        }
        // Center "Before you continue", "What you'll find here", and "What you won't find"
        const shouldCenter = section.section_key === 'principle_title' ||
                            section.section_key === 'features_intro' ||
                            section.section_key === 'wont_find_title';
        return (
          <h3 key={section.id} style={{
            textAlign: shouldCenter ? 'center' : 'left',
            color: colors.text.dark,
            fontSize: '1.3rem',
            marginBottom: '0px',
            marginTop: '5px'
          }}>
            {title}
          </h3>
        );

      case 'paragraph':
        // Process markdown bold formatting in paragraphs
        const processedContent = content ? content.replace(
          /\*\*([^*]+)\*\*/g,
          '<strong style="color: #030303; font-weight: 600;">$1</strong>'
        ) : '';
        return <p key={section.id} dangerouslySetInnerHTML={{ __html: processedContent }} />;

      case 'list':
        const items = content ? content.split('|') : [];
        if (useTwoColumns) {
          return (
            <TwoColumnList key={section.id}>
              {items.map((item, index) => (
                <li key={`${section.id}-${index}`}>{makeBoldWords(item)}</li>
              ))}
            </TwoColumnList>
          );
        }
        return (
          <ul key={section.id} style={{ paddingLeft: '30px', fontSize: '0.85rem', marginTop: '4px', marginBottom: '5px', lineHeight: '1', color: colors.text.light }}>
            {items.map((item, index) => (
              <li key={`${section.id}-${index}`}>{makeBoldWords(item)}</li>
            ))}
          </ul>
        );

      case 'highlight':
        // Center specific highlight sections
        const shouldCenterHighlight = section.section_key === 'disclaimer_closing' || section.section_key === 'why_statement';
        return (
          <p key={section.id} style={{ marginTop: '25px', fontStyle: 'italic', color: colors.accent.primary, textAlign: shouldCenterHighlight ? 'center' : 'left' }}>
            {content}
          </p>
        );

      case 'tagline':
        // Tagline can use either title or content
        const taglineText = title || content;
        // Center the final tagline "Fck Insomnia. Reclaim the night."
        const isFinalTagline = section.section_key === 'tagline';
        // Make "There are as many insomnias..." smaller
        const isWhyTitle = section.section_key === 'why_title';
        const fontSize = isWhyTitle ? '1rem' : '1.3rem';
        return (
          <p key={section.id} style={{ fontWeight: 'bold', color: colors.base.primary, fontSize, marginTop: '0px', marginBottom: '5px', textAlign: isFinalTagline ? 'center' : 'left' }}>
            {taglineText}
          </p>
        );

        case 'button':
          if(section.section_key === 'contact_me'){
          return (
            <ButtonRow>
              <ContactButton
                onClick={() => {
                  setIsOpen(false);
                  setTimeout(() => setIsContactOpen(true), 300);
                }}
              >
                <IconMail size={15} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> {t('common.contactMe')}
              </ContactButton>
            </ButtonRow>
          );
        }else{
          return;
        }

      default:
        return <p key={section.id}>{content}</p>;
    }
  };

  // Separate content sections
  const aboutLang = currentLanguage?.startsWith('fr') ? 'fr' : 'en';
  const taglineSection = aboutContent.find(s => s.section_key === 'tagline');
  const fixedTitle = taglineSection
    ? (taglineSection[`title_${aboutLang}`] || taglineSection[`content_${aboutLang}`])
    : 'Fuck Insomnia. Reclaim the night.';

  // Page 2: Why section (Everyone must experiment...) + Contribute
  const page2Content = aboutContent.filter(
    s => (s.display_order >= 10 && s.display_order <= 13) ||
         (s.display_order >= 22 && s.display_order <= 25)
  );

  // Get story content for current language
  const lang = currentLanguage?.startsWith('fr') ? 'fr' : 'en';
  const storyTitle = storyData ? storyData[`title_${lang}`] : '';
  const storyContentRaw = storyData ? storyData[`content_${lang}`] : '';

  // Convert markdown to HTML for display
  const convertMarkdownToHtml = (text) => {
    if (!text) return '';

    // Swap the emoji markers stored in the DB for the site's stroke-style icons
    let html = replaceEmojisWithIcons(text);

    // Bold - handle first
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic - handle after bold is already converted
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

    // Process lists line by line to properly group them
    const lines = html.split('\n');
    const processedLines = [];
    let currentList = null;
    let listItems = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Ordered list item
      if (/^\d+\.\s+(.+)$/.test(line)) {
        const match = line.match(/^\d+\.\s+(.+)$/);
        if (currentList !== 'ol') {
          if (currentList === 'ul') {
            processedLines.push('<ul>' + listItems.join('') + '</ul>');
            listItems = [];
          }
          currentList = 'ol';
        }
        listItems.push('<li>' + match[1] + '</li>');
      }
      // Unordered list item
      else if (/^-\s+(.+)$/.test(line)) {
        const match = line.match(/^-\s+(.+)$/);
        if (currentList !== 'ul') {
          if (currentList === 'ol') {
            processedLines.push('<ol>' + listItems.join('') + '</ol>');
            listItems = [];
          }
          currentList = 'ul';
        }
        listItems.push('<li>' + match[1] + '</li>');
      }
      // Regular line
      else {
        if (currentList) {
          processedLines.push(`<${currentList}>` + listItems.join('') + `</${currentList}>`);
          listItems = [];
          currentList = null;
        }
        processedLines.push(line);
      }
    }

    // Close any remaining list
    if (currentList) {
      processedLines.push(`<${currentList}>` + listItems.join('') + `</${currentList}>`);
    }

    html = processedLines.join('<br />');

    return html;
  };

  const storyContent = convertMarkdownToHtml(storyContentRaw);

  // Navigation handlers
  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageIndex) => {
    setCurrentPage(pageIndex);
  };

  // Handle story edit
  const handleOpenStoryEdit = () => {
    if (storyData) {
      setStoryEditData({ ...storyData });
      setIsStoryEditOpen(true);
    }
  };

  const handleSaveStory = async () => {
    if (!storyEditData) return;

    try {
      const response = await fetch('/api/story/content', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(storyEditData),
      });

      const data = await response.json();

      if (data.success) {
        setStoryData({ ...storyEditData });
        setIsStoryEditOpen(false);
        alert('Story updated successfully!');
      } else {
        alert('Failed to update story: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving story:', error);
      alert('Error saving story: ' + error.message);
    }
  };

  // Swipe gesture handlers for mobile
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    } else if (isRightSwipe && currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }

    // Reset touch values
    touchStartX.current = null;
    touchEndX.current = null;
  }, [currentPage, totalPages]);

  // Render current page content
  const renderPageContent = (pageIndex) => {
    switch(pageIndex) {
      case 0:
        // Page 1: Before you continue - included/excluded feature breakdown
        return (
          <PageContent
            key={pageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ContentWrapper>
              <DisclaimerColumns>
                <DisclaimerColumn $variant="include">
                  <DisclaimerColumnTitle $variant="include">
                    <IconIncludeHeader />
                    {t('disclaimer.includeTitle')}
                    <IconCheck size={14} />
                  </DisclaimerColumnTitle>

                  <DisclaimerTileGrid>
                    <DisclaimerTile $variant="include">
                      <DisclaimerTileIcon $variant="include"><IconTree /></DisclaimerTileIcon>
                      <DisclaimerTileTitle $variant="include">{t('disclaimer.tileTreeTitle')}</DisclaimerTileTitle>
                      <DisclaimerTileBody>{t('disclaimer.tileTreeBody')}</DisclaimerTileBody>
                    </DisclaimerTile>
                    <DisclaimerTile $variant="include">
                      <DisclaimerTileIcon $variant="include"><IconRobot /></DisclaimerTileIcon>
                      <DisclaimerTileTitle $variant="include">{t('disclaimer.tileAiTitle')}</DisclaimerTileTitle>
                      <DisclaimerTileBody>{t('disclaimer.tileAiBody')}</DisclaimerTileBody>
                    </DisclaimerTile>
                    <DisclaimerTile $variant="include">
                      <DisclaimerTileIcon $variant="include"><IconStopwatch /></DisclaimerTileIcon>
                      <DisclaimerTileTitle $variant="include">{t('disclaimer.tileDurationTitle')}</DisclaimerTileTitle>
                      <DisclaimerTileBody>{t('disclaimer.tileDurationBody')}</DisclaimerTileBody>
                    </DisclaimerTile>
                    <DisclaimerTile $variant="include">
                      <DisclaimerTileIcon $variant="include"><IconAvatar /></DisclaimerTileIcon>
                      <DisclaimerTileTitle $variant="include">{t('disclaimer.tileProfessionalTitle')}</DisclaimerTileTitle>
                      <DisclaimerTileBody>{t('disclaimer.tileProfessionalBody')}</DisclaimerTileBody>
                    </DisclaimerTile>
                  </DisclaimerTileGrid>

                  <LoggedInBox>
                    <LoggedInTitle><IconLock size={13} /> {t('disclaimer.loggedInTitle')}</LoggedInTitle>
                    <PillRow>
                      <Pill>✗ {t('disclaimer.tried')}</Pill>
                      <Pill><IconBulb size={12} /> {t('disclaimer.mySolutions')}</Pill>
                      <Pill>★ {t('disclaimer.favorites')}</Pill>
                    </PillRow>
                    <DocLine>
                      <IconDocument />
                      {t('disclaimer.printable')}
                    </DocLine>
                  </LoggedInBox>
                </DisclaimerColumn>

                <DisclaimerColumn $variant="exclude">
                  <DisclaimerColumnTitle $variant="exclude">
                    <IconExcludeHeader />
                    {t('disclaimer.excludeTitle')}
                    <IconCross size={14} />
                  </DisclaimerColumnTitle>

                  <DisclaimerTileGrid>
                    <DisclaimerTile $variant="exclude">
                      <DisclaimerTileIcon $variant="exclude"><IconNoDiagnosis /></DisclaimerTileIcon>
                      <DisclaimerTileTitle $variant="exclude">{t('disclaimer.tileNoDiagnosisTitle')}</DisclaimerTileTitle>
                      <DisclaimerTileBody>{t('disclaimer.tileNoDiagnosisBody')}</DisclaimerTileBody>
                    </DisclaimerTile>
                    <DisclaimerTile $variant="exclude">
                      <DisclaimerTileIcon $variant="exclude"><IconNoCertified /></DisclaimerTileIcon>
                      <DisclaimerTileTitle $variant="exclude">{t('disclaimer.tileNoCertifiedTitle')}</DisclaimerTileTitle>
                      <DisclaimerTileBody>{t('disclaimer.tileNoCertifiedBody')}</DisclaimerTileBody>
                    </DisclaimerTile>
                    <DisclaimerTile $variant="exclude" $wide>
                      <DisclaimerTileIcon $variant="exclude"><IconNoCoaching /></DisclaimerTileIcon>
                      <DisclaimerTileTitle $variant="exclude">{t('disclaimer.tileNoCoachingTitle')}</DisclaimerTileTitle>
                      <DisclaimerTileBody>{t('disclaimer.tileNoCoachingBody')}</DisclaimerTileBody>
                    </DisclaimerTile>
                  </DisclaimerTileGrid>
                </DisclaimerColumn>
              </DisclaimerColumns>
            </ContentWrapper>
          </PageContent>
        );

      case 1:
        // Page 2: Insomnia problem panel + Contribute panel
        const whyOutsideContent = page2Content.filter(s => s.section_key === 'why_statement');

        return (
          <PageContent
            key={pageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ContentWrapper>
              <ProblemPanel>
                <ProblemHeader>{t('intro.problemHeader')}</ProblemHeader>
                <ProblemParagraph dangerouslySetInnerHTML={{ __html: t('intro.problemParagraph1') }} />
                <ProblemParagraph dangerouslySetInnerHTML={{ __html: t('intro.problemParagraph2') }} />
              </ProblemPanel>

              <ModalText $large>
                {whyOutsideContent.map(section => renderContent(section, false))}
              </ModalText>

              <ContributePanel>
                <ContributeHeader>{t('intro.contributeHeader')}</ContributeHeader>
                <ContributeIntro>{t('intro.contributeIntro')}</ContributeIntro>

                <TileGrid>
                  <Tile>
                    <TileIcon><IconFeedback /></TileIcon>
                    <TileTitle>{t('intro.tileFeedbackTitle')}</TileTitle>
                    <TileBody>{t('intro.tileFeedbackBody')}</TileBody>
                  </Tile>
                  <Tile>
                    <TileIcon><IconIdeas /></TileIcon>
                    <TileTitle>{t('intro.tileIdeasTitle')}</TileTitle>
                    <TileBody>{t('intro.tileIdeasBody')}</TileBody>
                  </Tile>
                  <Tile>
                    <TileIcon><IconMegaphone /></TileIcon>
                    <TileTitle>{t('intro.tileSpreadTitle')}</TileTitle>
                    <TileBody>{t('intro.tileSpreadBody')}</TileBody>
                  </Tile>
                </TileGrid>

                <AccentContactButton
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => setIsContactOpen(true), 300);
                  }}
                >
                  <IconMail size={15} /> {t('common.contactMe')}
                </AccentContactButton>
              </ContributePanel>
            </ContentWrapper>
          </PageContent>
        );

      case 2:
        // Page 3: Support the project
        return (
          <PageContent
            key={pageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ContentWrapper>
              <ContributePanel>
                <ContributeHeader>
                  <IconHeart size={16} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
                  {t('intro.supportHeader')}
                </ContributeHeader>
                <ContributeIntro style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{t('intro.supportIntro')}</ContributeIntro>
                <ContributeIntro style={{ fontWeight: 700, textAlign: 'center' }}>{t('intro.supportSubtitle')}</ContributeIntro>

                <TileGrid>
                  <Tile>
                    <TileIcon><IconServer /></TileIcon>
                    <TileTitle>{t('intro.tileRunningTitle')}</TileTitle>
                    <TileBody>{t('intro.tileRunningBody')}</TileBody>
                  </Tile>
                  <Tile>
                    <TileIcon><IconSparkles /></TileIcon>
                    <TileTitle>{t('intro.tileFeaturesTitle')}</TileTitle>
                    <TileBody>{t('intro.tileFeaturesBody')}</TileBody>
                  </Tile>
                  <Tile>
                    <TileIcon><IconHeart /></TileIcon>
                    <TileTitle>{t('intro.tileExperienceTitle')}</TileTitle>
                    <TileBody>{t('intro.tileExperienceBody')}</TileBody>
                  </Tile>
                </TileGrid>
              </ContributePanel>
            </ContentWrapper>

            {/* PayPal donation section */}
            <div style={{ marginTop: '20px' }}>
              <DonateSection>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{
                    display: 'block',
                    color: colors.base.themePrimary,
                    marginBottom: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    {t('common.paymentAmountLabel')}
                  </label>
                  <input
                    id="donation-amount"
                    type="number"
                    min="1"
                    step="1"
                    defaultValue="1"
                    inputMode="decimal"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border.gray}`,
                      background: colors.background.lightGray,
                      color: colors.text.dark,
                      fontSize: '1rem'
                    }}
                  />
                  <div style={{ color: colors.base.themePrimary, fontSize: '0.85rem', marginTop: '6px', textAlign: 'left', opacity: 0.75 }}>
                    {t('common.minimumAmount')}
                  </div>
                </div>

                <div style={{ width: '100%' }}>
                  <div
                    id="paypal-about-container"
                    className="paypal-container"
                    style={{ width: '100%', minHeight: '50px' }}
                  ></div>
                </div>
              </DonateSection>
            </div>
          </PageContent>
        );

      default:
        return null;
    }
  };

  // Modal content to render in portal
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={!wasAutoOpened || currentPage === 2 ? handleClose : undefined}
        >
          <ModalContent
            $paginated
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {(!wasAutoOpened || currentPage === 2) && <CloseButton ref={closeButtonRef} onClick={handleClose}>×</CloseButton>}

            {loading ? (
              <ModalText $large>
                <p>{t('common.loading') || 'Loading...'}</p>
              </ModalText>
            ) : (
              <>
                {/* Fixed Title at top */}
                <ModalTitle style={{ textAlign: 'center', marginBottom: '30px' }}>
                  {fixedTitle}
                </ModalTitle>

                {/* Page Content with AnimatePresence for smooth transitions */}
                <AnimatePresence mode="wait">
                  {renderPageContent(currentPage)}
                </AnimatePresence>

                {/* Page Indicator */}
                <ArrowButton
                  direction="left"
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                >
                  ‹
                </ArrowButton>
                <div>
                  <PageIndicator>
                    {[...Array(totalPages)].map((_, index) => (
                      <PageDot
                        key={index}
                        $active={index === currentPage}
                        onClick={() => handlePageClick(index)}
                      />
                    ))}
                  </PageIndicator>
                  {wasAutoOpened && currentPage < 2 && (
                    <PageHint>
                      {t('common.navigateToLastPageToClose') || 'Navigate to the last page to close'}
                    </PageHint>
                  )}
                </div>
                <ArrowButton
                  ref={rightArrowRef}
                  direction="right"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages - 1}
                >
                  ›
                </ArrowButton>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );

  // My Story modal content
  const storyModalContent = (
    <AnimatePresence>
      {isStoryOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsStoryOpen(false)}
        >
          <ModalContent
            $scrollInner
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={() => setIsStoryOpen(false)}>×</CloseButton>

            {isAdmin && (
              <EditStoryButton onClick={handleOpenStoryEdit} title="Edit Story">
                ✏️
              </EditStoryButton>
            )}

            {storyLoading ? (
              <ModalText $large>
                <p>{t('common.loading') || 'Loading...'}</p>
              </ModalText>
            ) : (
              <>
                <ModalTitle style={{ textAlign: 'center', marginBottom: '30px' }}>
                  {storyTitle || 'My Story'}
                </ModalTitle>
                <ScrollableBody>
                  <ContentWrapper style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <WhiteContentBox $scrollInner>
                      <div
                        dangerouslySetInnerHTML={{ __html: storyContent }}
                        style={{
                          color: '#333',
                          lineHeight: '1.6',
                          fontSize: '0.9rem'
                        }}
                      />
                    </WhiteContentBox>
                  </ContentWrapper>
                </ScrollableBody>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );

  // Story edit modal content
  const storyEditModalContent = (
    <AnimatePresence>
      {isStoryEditOpen && storyEditData && (
        <>
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsStoryEditOpen(false)}
          />
          <StoryEditModal
            initial={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
            animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
            transition={{ type: 'spring', damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              // Force perfect centering with CSS transform
              transform: 'translate(-50%, -50%)',
              // Ensure transform is applied
              willChange: 'transform'
            }}
          >
            <h3>✏️ Edit My Story</h3>
            
            <label>Title (English)</label>
            <input
              type="text"
              value={storyEditData.title_en || ''}
              onChange={(e) => setStoryEditData({ ...storyEditData, title_en: e.target.value })}
              placeholder="When the Night Refuses to Sleep"
            />

            <label>Title (French)</label>
            <input
              type="text"
              value={storyEditData.title_fr || ''}
              onChange={(e) => setStoryEditData({ ...storyEditData, title_fr: e.target.value })}
              placeholder="Mon Histoire"
            />

            <label>Content (English)</label>
            <RichTextEditor
              value={storyEditData.content_en || ''}
              onChange={(e) => setStoryEditData({ ...storyEditData, content_en: e.target.value })}
              placeholder="Enter story content..."
            />

            <label>Content (French)</label>
            <RichTextEditor
              value={storyEditData.content_fr || ''}
              onChange={(e) => setStoryEditData({ ...storyEditData, content_fr: e.target.value })}
              placeholder="Entrez le contenu de l'histoire..."
            />

            <EditModalActions>
              <EditActionButton onClick={() => setIsStoryEditOpen(false)}>
                Cancel
              </EditActionButton>
              <EditActionButton primary onClick={handleSaveStory}>
                💾 Save
              </EditActionButton>
            </EditModalActions>
          </StoryEditModal>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <ButtonContainer>
        <AboutButton
          onClick={handleOpen}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <IconInfo size={15} /> {t('aboutMe') || 'Welcome !'}
        </AboutButton>
        <AboutButton
          onClick={() => setIsStoryOpen(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <IconBook size={15} /> {t('myStory') || 'My Story'}
        </AboutButton>
        {isDevelopment && toggleAdminMode && (
          <AdminToggleButton
            onClick={toggleAdminMode}
            $active={isAdmin}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            {isAdmin ? '👑 Admin ON' : '👤 User Mode'}
          </AdminToggleButton>
        )}
        {isAdmin && (
          <AboutButton
            onClick={handleTestFirstTime}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            🔔 Test First Time
          </AboutButton>
        )}
      </ButtonContainer>

      {/* Render modal in portal to escape stacking context */}
      {createPortal(modalContent, document.body)}

      {/* Render ripple effects outside modal's clipping context */}
      {isOpen && wasAutoOpened && rightArrowPos && currentPage >= 0 && currentPage < 2 && createPortal(
        <Ripple
          style={{
            top: `${rightArrowPos.top}px`,
            left: `${rightArrowPos.left}px`,
            width: `${rightArrowPos.width}px`,
            height: `${rightArrowPos.height}px`
          }}
        />,
        document.body
      )}
      
      {isOpen && wasAutoOpened && closeButtonPos && currentPage === 2 && createPortal(
        <CloseButtonRipple
          style={{
            top: `${closeButtonPos.top}px`,
            left: `${closeButtonPos.left}px`,
            width: `${closeButtonPos.width}px`,
            height: `${closeButtonPos.height}px`
          }}
        />,
        document.body
      )}

      {/* Render My Story modal in portal */}
      {createPortal(storyModalContent, document.body)}

      {/* Render Story Edit modal in portal */}
      {createPortal(storyEditModalContent, document.body)}

      {/* Contact Modal */}
      <ContactFormModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        onBack={() => {
          setIsContactOpen(false);
          setTimeout(() => setIsOpen(true), 300);
        }}
      />

      <PaymentSuccessModal
        isOpen={showThankYou}
        onClose={() => setShowThankYou(false)}
        isAuthenticated={!!user}
      />
    </>
  );
};

export default AboutMeButton;

